/**
 * Memórias — todas as fotos do grupo, em grade quadrada, agrupadas por dia
 * (decisão do PO, 17/09/2026, lote de aparência 2: "álbum simbólico" = o dia
 * do roteiro; nada de álbum como conceito). O "+" para enviar fica à direita
 * do cabeçalho, o mesmo lugar das demais telas internas.
 *
 * Sem comentários, reações ou favoritos — as conversas seguem no WhatsApp
 * (decisão de produto). O que existe é: enviar, ver por dia, abrir, baixar,
 * compartilhar, apagar o que é seu.
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useTrip } from '../../trip/TripContext';
import { Icon } from '../../ui/icons';
import { AtalhoDoPassaporte } from '../carimbos/AtalhoDoPassaporte';
import { EmptyState, ErrorState, Skeleton } from '../../ui/primitives';
import { type Foto, memoriasApi } from './api';
import { ddmm, plural, useAtualizarAoEnviar, useConfigMedia, useResumoDasMemorias } from './comum';
import { resumoDaFila, useFila } from './fila';

/** Uma página grande o bastante para o beta (a API limita a 200). */
const POR_PAGINA = 200;

export function MemoriasScreen() {
  const { tripId } = useTrip();
  const navegar = useNavigate();
  const config = useConfigMedia();
  const resumo = useResumoDasMemorias(tripId);
  const fotos = useQuery({
    queryKey: ['memorias', tripId, 'todas', POR_PAGINA],
    queryFn: () => memoriasApi.listar(tripId, undefined, { limit: POR_PAGINA }),
    enabled: tripId.length > 0,
    staleTime: 30_000,
  });
  const todosOsItens = useFila((s) => s.itens);
  const fila = useMemo(() => todosOsItens.filter((i) => i.tripId === tripId), [todosOsItens, tripId]);
  const andamento = resumoDaFila(fila);
  useAtualizarAoEnviar(tripId);
  // Quando a fila termina, a barra verde fica ~2,7s e some.
  const [recemEnviadas, setRecemEnviadas] = useState(0);
  useEffect(() => {
    if (!andamento.terminou || andamento.total === 0 || andamento.erros > 0) return;
    setRecemEnviadas(andamento.total);
    const t = window.setTimeout(() => setRecemEnviadas(0), 2700);
    return () => window.clearTimeout(t);
  }, [andamento.terminou, andamento.total, andamento.erros]);

  const podeEnviar = config.data?.media === true;

  // Agrupa por dia na ordem do resumo (que já vem por data), para o título
  // do dia vir do servidor e as fotos, da lista.
  const porDia = useMemo(() => {
    const dias = resumo.data?.days ?? [];
    const itens = fotos.data?.items ?? [];
    const mapa = new Map<string, Foto[]>();
    for (const f of itens) mapa.set(f.dayId, [...(mapa.get(f.dayId) ?? []), f]);
    return dias.map((d) => ({ ...d, fotos: mapa.get(d.dayId) ?? [] }));
  }, [resumo.data, fotos.data]);

  const carregando = resumo.isLoading || fotos.isLoading;
  const erro = resumo.isError || fotos.isError;

  return (
    <section className="screen mem">
      <header className="mem__cabecalho">
        <div>
          <h1 className="mem__titulo">Memórias</h1>
        </div>
        {podeEnviar && (
          <button type="button" className="mem__mais" onClick={() => navegar('/memorias/enviar')} aria-label="Enviar foto" data-testid="mem-enviar">
            <Icon name="criar" size={22} />
          </button>
        )}
      </header>

      {/* Fila de envio (refino final): barra tinta com contador e trilho de
          progresso; ao terminar sem erro, fica verde por um instante. */}
      {andamento.total > 0 && (!andamento.terminou || recemEnviadas > 0) && andamento.erros === 0 && (
        <Link to="/memorias/enviar" className={andamento.terminou ? 'mem__fila mem__fila--ok nv-enter' : 'mem__fila nv-enter'}>
          <span className="mem__fila-linha">
            <span className="mem__fila-contador">{andamento.terminou ? andamento.total : andamento.ativas}</span>
            <span>{andamento.terminou ? plural(andamento.total, 'foto enviada', 'fotos enviadas') : `Enviando… ${andamento.concluidas} de ${andamento.total}`}</span>
          </span>
          <span className="mem__fila-trilho" aria-hidden="true">
            <span style={{ width: `${Math.round((andamento.concluidas / Math.max(1, andamento.total)) * 100)}%` }} />
          </span>
        </Link>
      )}
      {andamento.erros > 0 && andamento.terminou && (
        <Link to="/memorias/enviar" className="mem__fila mem__fila--erro">
          <span className="mem__fila-contador">{andamento.erros}</span>
          <span>{plural(andamento.erros, 'foto não subiu', 'fotos não subiram')} · toque para tentar de novo</span>
        </Link>
      )}

      <AtalhoDoPassaporte />

      {carregando && (
        <>
          <Skeleton height={70} />
          <Skeleton height={220} count={2} />
        </>
      )}
      {!carregando && erro && (
        <ErrorState
          title="Não foi possível carregar"
          description="Verifique a conexão e tente de novo."
          onRetry={() => {
            void resumo.refetch();
            void fotos.refetch();
          }}
        />
      )}
      {!carregando && !erro && porDia.length === 0 && (
        <EmptyState
          ilustracao="memorias"
          title="Nenhuma foto ainda"
          description={podeEnviar ? 'As fotos do grupo ficam aqui, por dia da viagem.' : 'O envio de fotos não está ligado neste ambiente.'}
          {...(podeEnviar ? { action: { label: 'Enviar as primeiras fotos', onClick: () => navegar('/memorias/enviar') } } : {})}
        />
      )}
      {!carregando &&
        !erro &&
        porDia.map((d) => (
          <section key={d.dayId} className="mem__dia" data-testid="mem-dia">
            <Link to={`/memorias/${d.date}`} className="mem__dia-cab" aria-label={`Álbum de ${ddmm(d.date)}`}>
              <span className="mem__dia-titulo">
                <span className="mem__local">{d.title ?? `Dia ${ddmm(d.date)}`}</span>
                <span className="mem__contagem">
                  {ddmm(d.date)} · {plural(d.photos, 'foto', 'fotos')}
                  {d.uploaders.length > 0 && ` · ${d.uploaders.slice(0, 2).join(', ')}${d.uploaders.length > 2 ? ` +${d.uploaders.length - 2}` : ''}`}
                </span>
              </span>
              <Icon name="avancar" size={18} />
            </Link>
            <div className="mem__grade">
              {(d.fotos.length > 0 ? d.fotos : []).map((f) => (
                <Link key={f.id} to={`/memorias/${d.date}/${f.id}`} className="mem__quadrado" aria-label={`Foto de ${f.uploader.name}`}>
                  <img src={f.thumbUrl} alt="" loading="lazy" />
                </Link>
              ))}
              {d.fotos.length === 0 &&
                d.thumbUrls.map((u, i) => (
                  <Link key={`r${i}`} to={`/memorias/${d.date}`} className="mem__quadrado" aria-label={`Álbum de ${ddmm(d.date)}`}>
                    <img src={u} alt="" loading="lazy" />
                  </Link>
                ))}
            </div>
          </section>
        ))}
      {fotos.data?.nextBefore && (
        <p className="mem__sub">Mostrando as {POR_PAGINA} fotos mais recentes. Abra um dia para ver todas as dele.</p>
      )}
    </section>
  );
}
